import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

import {
  pointActivityService,
  type MeaningfulActionCode,
  type PointApiResponse,
  type PointHeartbeatMode,
} from '../services/pointActivityService'
import { useAuthStore } from '../store/authStore'
import {
  maskPointIdentifier,
  pointDebug,
  pointDebugError,
  pointDebugWarn,
  summarizePointResponse,
} from '../utils/pointDebug'

const HEARTBEAT_INTERVAL_MS = 30 * 1000
const LIVE_PROGRESS_INTERVAL_MS = 1000
const RECENT_INTERACTION_MS = 45 * 1000
const HEARTBEAT_MAX_CREDIT_SECONDS = 30

function shouldContinueTracking(response: PointApiResponse): boolean {
  const review = response.dailyReview

  return (
    review.eligible &&
    !review.rewardedToday &&
    review.rewardedDaysThisCycle < review.maxRewardsPerCycle &&
    response.wallet.pointsTotal < response.wallet.maxPointsBalance
  )
}

function pointErrorMessage(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

function trackingDecision(response: PointApiResponse) {
  return {
    continueTracking: shouldContinueTracking(response),
    eligible: response.dailyReview.eligible,
    ineligibleReason: response.dailyReview.ineligibleReason,
    rewardedToday: response.dailyReview.rewardedToday,
    rewardedDaysThisCycle: response.dailyReview.rewardedDaysThisCycle,
    maxRewardsPerCycle: response.dailyReview.maxRewardsPerCycle,
    pointsTotal: response.wallet.pointsTotal,
    maxPointsBalance: response.wallet.maxPointsBalance,
    activeSeconds: response.dailyReview.activeSeconds,
    requiredActiveSeconds: response.dailyReview.requiredActiveSeconds,
    meaningfulActions: response.dailyReview.meaningfulActions,
    requiredMeaningfulActions: response.dailyReview.requiredMeaningfulActions,
  }
}

export function usePointActivity(
  accountId?: string,
  meaningfulActionCode?: MeaningfulActionCode,
  meaningfulActionRelatedEntityId?: string | null
) {
  const status = useAuthStore((state) =>
    accountId ? state.pointStatusByAccount[accountId] ?? null : null
  )
  const error = useAuthStore((state) =>
    accountId ? state.pointErrorByAccount[accountId] ?? null : null
  )
  const setPointStatus = useAuthStore((state) => state.setPointStatus)
  const setPointLiveActiveSeconds = useAuthStore(
    (state) => state.setPointLiveActiveSeconds
  )
  const setPointError = useAuthStore((state) => state.setPointError)
  const updateAccountWallet = useAuthStore((state) => state.updateAccountWallet)
  const liveActiveSeconds = useAuthStore((state) =>
    accountId
      ? state.pointLiveActiveSecondsByAccount[accountId] ??
          state.pointStatusByAccount[accountId]?.dailyReview.activeSeconds ??
          0
      : 0
  )
  const [loading, setLoading] = useState(false)
  const lastInteractionRef = useRef(0)
  const heartbeatInFlightRef = useRef(false)

  const applyResponse = useCallback(
    (response: PointApiResponse) => {
      if (!accountId) {
        pointDebugWarn('tracker.applyResponse.no_account', {
          point: summarizePointResponse(response),
        })
        return
      }

      pointDebug('tracker.applyResponse', {
        accountId: maskPointIdentifier(accountId),
        point: summarizePointResponse(response),
      })

      setPointStatus(accountId, response)
      updateAccountWallet(accountId, {
        pointsTotal: response.wallet.pointsTotal,
        coinsTotal: response.wallet.coinsTotal,
        pointsResetAt: response.wallet.pointsResetAt,
        pointsCycleStartedAt: response.wallet.pointsCycleStartedAt,
      })

      if (response.rewardGranted || (response.pointsAwarded ?? 0) > 0) {
        pointDebug('tracker.reward_applied', {
          accountId: maskPointIdentifier(accountId),
          pointsAwarded: response.pointsAwarded ?? 0,
          pointsTotal: response.wallet.pointsTotal,
        })
      }
    },
    [accountId, setPointStatus, updateAccountWallet]
  )

  const refresh = useCallback(async () => {
    if (!accountId) {
      pointDebugWarn('tracker.refresh.skip', { reason: 'no_account_id' })
      return null
    }

    pointDebug('tracker.refresh.start', {
      accountId: maskPointIdentifier(accountId),
    })
    setLoading(true)
    setPointError(accountId, null)

    try {
      const response = await pointActivityService.getStatus(accountId)
      applyResponse(response)
      pointDebug('tracker.refresh.success', {
        accountId: maskPointIdentifier(accountId),
        decision: trackingDecision(response),
      })
      return response
    } catch (err) {
      const message = pointErrorMessage(err, 'Unable to load Daily Review status.')
      pointDebugError('tracker.refresh.error', {
        accountId: maskPointIdentifier(accountId),
        message,
      })
      setPointError(accountId, message)
      return null
    } finally {
      setLoading(false)
    }
  }, [accountId, applyResponse, setPointError])

  useEffect(() => {
    if (!accountId) {
      pointDebugWarn('tracker.mount.skip', {
        reason: 'no_account_id',
        meaningfulActionCode: meaningfulActionCode ?? null,
      })
      return
    }

    let cancelled = false
    let startupInFlight = true
    let trackingEnabled = false
    let meaningfulActionPending = Boolean(meaningfulActionCode)
    let meaningfulActionError: string | null = null
    let baselineRequired = true
    lastInteractionRef.current = 0

    pointDebug('tracker.mount', {
      accountId: maskPointIdentifier(accountId),
      meaningfulActionCode: meaningfulActionCode ?? null,
      relatedEntityId: maskPointIdentifier(meaningfulActionRelatedEntityId),
      visibility: document.visibilityState,
      heartbeatIntervalMs: HEARTBEAT_INTERVAL_MS,
      liveProgressIntervalMs: LIVE_PROGRESS_INTERVAL_MS,
      recentInteractionWindowMs: RECENT_INTERACTION_MS,
      baselineRequired,
    })

    const processResponse = (response: PointApiResponse) => {
      if (cancelled) {
        pointDebugWarn('tracker.response.ignored', {
          reason: 'cancelled',
          accountId: maskPointIdentifier(accountId),
        })
        return
      }

      applyResponse(response)
      trackingEnabled = shouldContinueTracking(response)
      pointDebug('tracker.decision', {
        accountId: maskPointIdentifier(accountId),
        ...trackingDecision(response),
        meaningfulActionPending,
        baselineRequired,
      })
    }

    const freezeLiveProgress = () => {
      const liveStatus = useAuthStore.getState().pointStatusByAccount[accountId]
      if (!liveStatus) return
      const currentLive =
        useAuthStore.getState().pointLiveActiveSecondsByAccount[accountId] ??
        liveStatus.dailyReview.activeSeconds
      const nextLive = liveStatus.dailyReview.activeSeconds
      if (currentLive !== nextLive) {
        setPointLiveActiveSeconds(accountId, nextLive)
      }
    }

    const setTrackingError = (err: unknown, fallback: string) => {
      if (cancelled) return
      const message = pointErrorMessage(err, fallback)
      pointDebugError('tracker.error', {
        accountId: maskPointIdentifier(accountId),
        message,
        meaningfulActionPending,
      })
      setPointError(accountId, message)
    }

    const heartbeatSkipReason = (mode: PointHeartbeatMode) => {
      if (cancelled) return 'cancelled'
      if (startupInFlight) return 'startup_in_flight'
      if (!trackingEnabled) return 'tracking_disabled'
      if (heartbeatInFlightRef.current) return 'already_in_flight'
      if (document.visibilityState !== 'visible') return 'page_hidden'
      if (lastInteractionRef.current === 0) return 'no_interaction'
      if (mode === 'active' && baselineRequired) return 'baseline_required'
      if (mode === 'baseline' && !baselineRequired) return 'baseline_already_established'
      const inactiveMs = Date.now() - lastInteractionRef.current
      if (inactiveMs > RECENT_INTERACTION_MS) return 'no_recent_interaction'
      return null
    }

    const sendHeartbeat = async (mode: PointHeartbeatMode = 'active') => {
      const skipReason = heartbeatSkipReason(mode)
      if (skipReason) {
        pointDebug('heartbeat.skip', {
          accountId: maskPointIdentifier(accountId),
          mode,
          reason: skipReason,
          trackingEnabled,
          baselineRequired,
          meaningfulActionPending,
          visibility: document.visibilityState,
          msSinceInteraction: Date.now() - lastInteractionRef.current,
        })
        return null
      }

      pointDebug('heartbeat.cycle.start', {
        accountId: maskPointIdentifier(accountId),
        mode,
        baselineRequired,
        meaningfulActionPending,
        msSinceInteraction: Date.now() - lastInteractionRef.current,
      })
      heartbeatInFlightRef.current = true

      try {
        await attemptMeaningfulAction()
        if (cancelled || !trackingEnabled) {
          pointDebug('heartbeat.after_action.stop', {
            accountId: maskPointIdentifier(accountId),
            cancelled,
            trackingEnabled,
            meaningfulActionPending,
          })
          return null
        }

        pointDebug('heartbeat.request', {
          accountId: maskPointIdentifier(accountId),
          mode,
        })

        try {
          const response = await pointActivityService.recordHeartbeat(accountId, mode)
          processResponse(response)
          if (mode === 'baseline') {
            baselineRequired = false
            setPointLiveActiveSeconds(accountId, response.dailyReview.activeSeconds)
            pointDebug('heartbeat.baseline.success', {
              accountId: maskPointIdentifier(accountId),
              activeSeconds: response.dailyReview.activeSeconds,
            })
          }
          pointDebug('heartbeat.success', {
            accountId: maskPointIdentifier(accountId),
            mode,
            point: summarizePointResponse(response),
          })

          if (meaningfulActionPending && meaningfulActionError && !cancelled) {
            pointDebugWarn('heartbeat.restore_action_error', {
              accountId: maskPointIdentifier(accountId),
              message: meaningfulActionError,
            })
            setPointError(accountId, meaningfulActionError)
          }
          return response
        } catch (err) {
          pointDebugError('heartbeat.error', {
            accountId: maskPointIdentifier(accountId),
            mode,
            message: pointErrorMessage(err, 'Unable to record Daily Review activity.'),
          })
          setTrackingError(err, 'Unable to record Daily Review activity.')
          return null
        }
      } finally {
        heartbeatInFlightRef.current = false
        pointDebug('heartbeat.cycle.end', {
          accountId: maskPointIdentifier(accountId),
          mode,
          trackingEnabled,
          meaningfulActionPending,
          baselineRequired,
        })
      }
    }

    const attemptMeaningfulAction = async () => {
      if (
        cancelled ||
        !trackingEnabled ||
        !meaningfulActionPending ||
        !meaningfulActionCode
      ) {
        pointDebug('action.skip', {
          accountId: maskPointIdentifier(accountId),
          cancelled,
          trackingEnabled,
          meaningfulActionPending,
          meaningfulActionCode: meaningfulActionCode ?? null,
        })
        return
      }

      pointDebug('action.attempt', {
        accountId: maskPointIdentifier(accountId),
        actionCode: meaningfulActionCode,
        relatedEntityId: maskPointIdentifier(meaningfulActionRelatedEntityId),
      })

      try {
        const response = await pointActivityService.recordMeaningfulAction(
          accountId,
          meaningfulActionCode,
          meaningfulActionRelatedEntityId
        )
        meaningfulActionPending = false
        meaningfulActionError = null
        processResponse(response)
        pointDebug('action.success', {
          accountId: maskPointIdentifier(accountId),
          actionCode: meaningfulActionCode,
          point: summarizePointResponse(response),
        })
      } catch (err) {
        meaningfulActionError = pointErrorMessage(
          err,
          'Unable to record Daily Review action.'
        )
        pointDebugError('action.error', {
          accountId: maskPointIdentifier(accountId),
          actionCode: meaningfulActionCode,
          message: meaningfulActionError,
          willRetry: true,
        })
        setTrackingError(err, 'Unable to record Daily Review action.')
      }
    }

    const markInteraction = () => {
      const previousInteractionAt = lastInteractionRef.current
      const now = Date.now()
      const interactionGapMs =
        previousInteractionAt > 0 ? now - previousInteractionAt : null
      const wasInactive =
        previousInteractionAt === 0 ||
        (interactionGapMs !== null && interactionGapMs > RECENT_INTERACTION_MS)

      lastInteractionRef.current = now

      if (wasInactive) {
        baselineRequired = true
        freezeLiveProgress()
      }

      pointDebug('tracker.interaction', {
        accountId: maskPointIdentifier(accountId),
        visibility: document.visibilityState,
        baselineRequired,
        wasInactive,
        interactionGapMs,
      })

      if (
        !cancelled &&
        trackingEnabled &&
        !startupInFlight &&
        document.visibilityState === 'visible' &&
        baselineRequired &&
        !heartbeatInFlightRef.current
      ) {
        void sendHeartbeat('baseline')
      }
    }

    const handleVisibilityChange = () => {
      pointDebug('visibility.change', {
        accountId: maskPointIdentifier(accountId),
        visibility: document.visibilityState,
      })
      if (document.visibilityState === 'hidden') {
        baselineRequired = true
        lastInteractionRef.current = 0
        freezeLiveProgress()
        return
      }

      if (document.visibilityState === 'visible') {
        baselineRequired = true
      }
    }

    const startTracking = async () => {
      pointDebug('startup.begin', {
        accountId: maskPointIdentifier(accountId),
        meaningfulActionCode: meaningfulActionCode ?? null,
      })
      setLoading(true)
      setPointError(accountId, null)

      try {
        let response: PointApiResponse
        try {
          pointDebug('startup.status.request', {
            accountId: maskPointIdentifier(accountId),
          })
          response = await pointActivityService.getStatus(accountId)
        } catch (err) {
          pointDebugError('startup.status.error', {
            accountId: maskPointIdentifier(accountId),
            message: pointErrorMessage(err, 'Unable to load Daily Review status.'),
          })
          setTrackingError(err, 'Unable to load Daily Review status.')
          trackingEnabled = false
          return
        }

        pointDebug('startup.status.success', {
          accountId: maskPointIdentifier(accountId),
          point: summarizePointResponse(response),
        })
        processResponse(response)

        if (!trackingEnabled || cancelled) {
          pointDebugWarn('startup.stop', {
            accountId: maskPointIdentifier(accountId),
            cancelled,
            decision: trackingDecision(response),
          })
          return
        }
      } finally {
        startupInFlight = false
        if (!cancelled) setLoading(false)
      }
    }

    const syncPresenceState = () => {
      if (!trackingEnabled || startupInFlight) {
        return
      }

      if (document.visibilityState !== 'visible') {
        baselineRequired = true
        lastInteractionRef.current = 0
        freezeLiveProgress()
        return
      }

      const stale =
        lastInteractionRef.current > 0 &&
        Date.now() - lastInteractionRef.current > RECENT_INTERACTION_MS

      if (stale) {
        baselineRequired = true
        lastInteractionRef.current = 0
        freezeLiveProgress()
      }
    }

    const events: Array<keyof WindowEventMap> = [
      'pointerdown',
      'pointermove',
      'keydown',
      'scroll',
      'touchstart',
    ]

    for (const eventName of events) {
      window.addEventListener(eventName, markInteraction, {
        passive: eventName !== 'keydown',
      })
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    void startTracking()

    const intervalId = window.setInterval(() => {
      syncPresenceState()
      pointDebug('heartbeat.interval.tick', {
        accountId: maskPointIdentifier(accountId),
        baselineRequired,
        trackingEnabled,
      })
      void sendHeartbeat('active')
    }, HEARTBEAT_INTERVAL_MS)

    const liveProgressIntervalId = window.setInterval(() => {
      if (!trackingEnabled || startupInFlight) {
        return
      }

      const store = useAuthStore.getState()
      const authoritativeStatus = store.pointStatusByAccount[accountId]
      const currentLive = store.pointLiveActiveSecondsByAccount[accountId]

      if (!authoritativeStatus) {
        return
      }

      const interactionStale =
        lastInteractionRef.current > 0 &&
        Date.now() - lastInteractionRef.current > RECENT_INTERACTION_MS

      if (interactionStale) {
        pointDebug('tracker.inactivity_freeze', {
          accountId: maskPointIdentifier(accountId),
          msSinceInteraction:
            Date.now() - lastInteractionRef.current,
        })

        baselineRequired = true
        lastInteractionRef.current = 0
        freezeLiveProgress()
        return
      }

      if (
        authoritativeStatus.dailyReview.rewardedToday ||
        authoritativeStatus.dailyReview.ineligibleReason !== null ||
        authoritativeStatus.dailyReview.rewardedDaysThisCycle >=
          authoritativeStatus.dailyReview.maxRewardsPerCycle ||
        authoritativeStatus.wallet.pointsTotal >=
          authoritativeStatus.wallet.maxPointsBalance ||
        document.visibilityState !== 'visible' ||
        baselineRequired ||
        lastInteractionRef.current <= 0
      ) {
        if (currentLive !== authoritativeStatus.dailyReview.activeSeconds) {
          setPointLiveActiveSeconds(
            accountId,
            authoritativeStatus.dailyReview.activeSeconds
          )
        }
        return
      }

      const serverSeconds = authoritativeStatus.dailyReview.activeSeconds
      const nextValue = Math.min(
        (currentLive ?? serverSeconds) + 1,
        serverSeconds + HEARTBEAT_MAX_CREDIT_SECONDS,
        authoritativeStatus.dailyReview.requiredActiveSeconds > 0
          ? authoritativeStatus.dailyReview.requiredActiveSeconds - 1
          : 0
      )
      const boundedValue = Math.max(serverSeconds, nextValue)
      if (boundedValue !== currentLive) {
        setPointLiveActiveSeconds(accountId, boundedValue)
      }
    }, LIVE_PROGRESS_INTERVAL_MS)

    return () => {
      cancelled = true
      pointDebug('tracker.unmount', {
        accountId: maskPointIdentifier(accountId),
        meaningfulActionCode: meaningfulActionCode ?? null,
      })
      window.clearInterval(intervalId)
      window.clearInterval(liveProgressIntervalId)
      for (const eventName of events) {
        window.removeEventListener(eventName, markInteraction)
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [
    accountId,
    meaningfulActionCode,
    meaningfulActionRelatedEntityId,
    applyResponse,
    setPointError,
    setPointLiveActiveSeconds,
  ])

  return { status, loading, error, refresh, liveActiveSeconds }
}
